"use client";

// packages/react/tooltip/src/Tooltip.tsx
import _toConsumableArray from "/vercel/path0/node_modules/@babel/runtime/helpers/esm/toConsumableArray.js";
import _objectWithoutProperties from "/vercel/path0/node_modules/@babel/runtime/helpers/esm/objectWithoutProperties.js";
import _defineProperty from "/vercel/path0/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
import _slicedToArray from "/vercel/path0/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
var _excluded = ["__scopeTooltip"],
  _excluded2 = ["forceMount", "side"],
  _excluded3 = ["__scopeTooltip", "children", "aria-label", "onEscapeKeyDown", "onPointerDownOutside"],
  _excluded4 = ["__scopeTooltip"];
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
import * as React from "react";
import { composeEventHandlers } from "@radix-ui/primitive";
import { useComposedRefs } from "@radix-ui/react-compose-refs";
import { createContextScope } from "@radix-ui/react-context";
import { DismissableLayer } from "@radix-ui/react-dismissable-layer";
import { useId } from "@radix-ui/react-id";
import * as PopperPrimitive from "@radix-ui/react-popper";
import { createPopperScope } from "@radix-ui/react-popper";
import { Portal as PortalPrimitive } from "@radix-ui/react-portal";
import { Presence } from "@radix-ui/react-presence";
import { Primitive } from "@radix-ui/react-primitive";
import { Slottable } from "@radix-ui/react-slot";
import { useControllableState } from "@radix-ui/react-use-controllable-state";
import * as VisuallyHiddenPrimitive from "@radix-ui/react-visually-hidden";
import { jsx, jsxs } from "react/jsx-runtime";
var _createContextScope = createContextScope("Tooltip", [createPopperScope]),
  _createContextScope2 = _slicedToArray(_createContextScope, 2),
  createTooltipContext = _createContextScope2[0],
  createTooltipScope = _createContextScope2[1];
var usePopperScope = createPopperScope();
var PROVIDER_NAME = "TooltipProvider";
var DEFAULT_DELAY_DURATION = 700;
var TOOLTIP_OPEN = "tooltip.open";
var _createTooltipContext = createTooltipContext(PROVIDER_NAME),
  _createTooltipContext2 = _slicedToArray(_createTooltipContext, 2),
  TooltipProviderContextProvider = _createTooltipContext2[0],
  useTooltipProviderContext = _createTooltipContext2[1];
var TooltipProvider = function TooltipProvider(props) {
  var __scopeTooltip = props.__scopeTooltip,
    _props$delayDuration = props.delayDuration,
    delayDuration = _props$delayDuration === void 0 ? DEFAULT_DELAY_DURATION : _props$delayDuration,
    _props$skipDelayDurat = props.skipDelayDuration,
    skipDelayDuration = _props$skipDelayDurat === void 0 ? 300 : _props$skipDelayDurat,
    _props$disableHoverab = props.disableHoverableContent,
    disableHoverableContent = _props$disableHoverab === void 0 ? false : _props$disableHoverab,
    children = props.children;
  var _React$useState = React.useState(true),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    isOpenDelayed = _React$useState2[0],
    setIsOpenDelayed = _React$useState2[1];
  var isPointerInTransitRef = React.useRef(false);
  var skipDelayTimerRef = React.useRef(0);
  React.useEffect(function () {
    var skipDelayTimer = skipDelayTimerRef.current;
    return function () {
      return window.clearTimeout(skipDelayTimer);
    };
  }, []);
  return /* @__PURE__ */jsx(TooltipProviderContextProvider, {
    scope: __scopeTooltip,
    isOpenDelayed: isOpenDelayed,
    delayDuration: delayDuration,
    onOpen: React.useCallback(function () {
      window.clearTimeout(skipDelayTimerRef.current);
      setIsOpenDelayed(false);
    }, []),
    onClose: React.useCallback(function () {
      window.clearTimeout(skipDelayTimerRef.current);
      skipDelayTimerRef.current = window.setTimeout(function () {
        return setIsOpenDelayed(true);
      }, skipDelayDuration);
    }, [skipDelayDuration]),
    isPointerInTransitRef: isPointerInTransitRef,
    onPointerInTransitChange: React.useCallback(function (inTransit) {
      isPointerInTransitRef.current = inTransit;
    }, []),
    disableHoverableContent: disableHoverableContent,
    children: children
  });
};
TooltipProvider.displayName = PROVIDER_NAME;
var TOOLTIP_NAME = "Tooltip";
var _createTooltipContext3 = createTooltipContext(TOOLTIP_NAME),
  _createTooltipContext4 = _slicedToArray(_createTooltipContext3, 2),
  TooltipContextProvider = _createTooltipContext4[0],
  useTooltipContext = _createTooltipContext4[1];
var Tooltip = function Tooltip(props) {
  var __scopeTooltip = props.__scopeTooltip,
    children = props.children,
    openProp = props.open,
    _props$defaultOpen = props.defaultOpen,
    defaultOpen = _props$defaultOpen === void 0 ? false : _props$defaultOpen,
    onOpenChange = props.onOpenChange,
    disableHoverableContentProp = props.disableHoverableContent,
    delayDurationProp = props.delayDuration;
  var providerContext = useTooltipProviderContext(TOOLTIP_NAME, props.__scopeTooltip);
  var popperScope = usePopperScope(__scopeTooltip);
  var _React$useState3 = React.useState(null),
    _React$useState4 = _slicedToArray(_React$useState3, 2),
    trigger = _React$useState4[0],
    setTrigger = _React$useState4[1];
  var contentId = useId();
  var openTimerRef = React.useRef(0);
  var disableHoverableContent = disableHoverableContentProp !== null && disableHoverableContentProp !== void 0 ? disableHoverableContentProp : providerContext.disableHoverableContent;
  var delayDuration = delayDurationProp !== null && delayDurationProp !== void 0 ? delayDurationProp : providerContext.delayDuration;
  var wasOpenDelayedRef = React.useRef(false);
  var _useControllableState = useControllableState({
      prop: openProp,
      defaultProp: defaultOpen,
      onChange: function onChange(open2) {
        if (open2) {
          providerContext.onOpen();
          document.dispatchEvent(new CustomEvent(TOOLTIP_OPEN));
        } else {
          providerContext.onClose();
        }
        onOpenChange === null || onOpenChange === void 0 ? void 0 : onOpenChange(open2);
      }
    }),
    _useControllableState2 = _slicedToArray(_useControllableState, 2),
    _useControllableState3 = _useControllableState2[0],
    open = _useControllableState3 === void 0 ? false : _useControllableState3,
    setOpen = _useControllableState2[1];
  var stateAttribute = React.useMemo(function () {
    return open ? wasOpenDelayedRef.current ? "delayed-open" : "instant-open" : "closed";
  }, [open]);
  var handleOpen = React.useCallback(function () {
    window.clearTimeout(openTimerRef.current);
    wasOpenDelayedRef.current = false;
    setOpen(true);
  }, [setOpen]);
  var handleClose = React.useCallback(function () {
    window.clearTimeout(openTimerRef.current);
    setOpen(false);
  }, [setOpen]);
  var handleDelayedOpen = React.useCallback(function () {
    window.clearTimeout(openTimerRef.current);
    openTimerRef.current = window.setTimeout(function () {
      wasOpenDelayedRef.current = true;
      setOpen(true);
    }, delayDuration);
  }, [delayDuration, setOpen]);
  React.useEffect(function () {
    return function () {
      return window.clearTimeout(openTimerRef.current);
    };
  }, []);
  return /* @__PURE__ */jsx(PopperPrimitive.Root, _objectSpread(_objectSpread({}, popperScope), {}, {
    children: /* @__PURE__ */jsx(TooltipContextProvider, {
      scope: __scopeTooltip,
      contentId: contentId,
      open: open,
      stateAttribute: stateAttribute,
      trigger: trigger,
      onTriggerChange: setTrigger,
      onTriggerEnter: React.useCallback(function () {
        if (providerContext.isOpenDelayed) handleDelayedOpen();else handleOpen();
      }, [providerContext.isOpenDelayed, handleDelayedOpen, handleOpen]),
      onTriggerLeave: React.useCallback(function () {
        if (disableHoverableContent) {
          handleClose();
        } else {
          window.clearTimeout(openTimerRef.current);
        }
      }, [handleClose, disableHoverableContent]),
      onOpen: handleOpen,
      onClose: handleClose,
      disableHoverableContent: disableHoverableContent,
      children: children
    })
  }));
};
Tooltip.displayName = TOOLTIP_NAME;
var TRIGGER_NAME = "TooltipTrigger";
var TooltipTrigger = React.forwardRef(function (props, forwardedRef) {
  var __scopeTooltip = props.__scopeTooltip,
    triggerProps = _objectWithoutProperties(props, _excluded);
  var context = useTooltipContext(TRIGGER_NAME, __scopeTooltip);
  var providerContext = useTooltipProviderContext(TRIGGER_NAME, __scopeTooltip);
  var popperScope = usePopperScope(__scopeTooltip);
  var ref = React.useRef(null);
  var composedRefs = useComposedRefs(forwardedRef, ref, context.onTriggerChange);
  var isPointerDownRef = React.useRef(false);
  var hasPointerMoveOpenedRef = React.useRef(false);
  var handlePointerUp = React.useCallback(function () {
    return isPointerDownRef.current = false;
  }, []);
  React.useEffect(function () {
    return function () {
      return document.removeEventListener("pointerup", handlePointerUp);
    };
  }, [handlePointerUp]);
  return /* @__PURE__ */jsx(PopperPrimitive.Anchor, _objectSpread(_objectSpread({
    asChild: true
  }, popperScope), {}, {
    children: /* @__PURE__ */jsx(Primitive.button, _objectSpread(_objectSpread({
      "aria-describedby": context.open ? context.contentId : void 0,
      "data-state": context.stateAttribute
    }, triggerProps), {}, {
      ref: composedRefs,
      onPointerMove: composeEventHandlers(props.onPointerMove, function (event) {
        if (event.pointerType === "touch") return;
        if (!hasPointerMoveOpenedRef.current && !providerContext.isPointerInTransitRef.current) {
          context.onTriggerEnter();
          hasPointerMoveOpenedRef.current = true;
        }
      }),
      onPointerLeave: composeEventHandlers(props.onPointerLeave, function () {
        context.onTriggerLeave();
        hasPointerMoveOpenedRef.current = false;
      }),
      onPointerDown: composeEventHandlers(props.onPointerDown, function () {
        isPointerDownRef.current = true;
        document.addEventListener("pointerup", handlePointerUp, {
          once: true
        });
      }),
      onFocus: composeEventHandlers(props.onFocus, function () {
        if (!isPointerDownRef.current) context.onOpen();
      }),
      onBlur: composeEventHandlers(props.onBlur, context.onClose),
      onClick: composeEventHandlers(props.onClick, context.onClose)
    }))
  }));
});
TooltipTrigger.displayName = TRIGGER_NAME;
var PORTAL_NAME = "TooltipPortal";
var _createTooltipContext5 = createTooltipContext(PORTAL_NAME, {
    forceMount: void 0
  }),
  _createTooltipContext6 = _slicedToArray(_createTooltipContext5, 2),
  PortalProvider = _createTooltipContext6[0],
  usePortalContext = _createTooltipContext6[1];
var TooltipPortal = function TooltipPortal(props) {
  var __scopeTooltip = props.__scopeTooltip,
    forceMount = props.forceMount,
    children = props.children,
    container = props.container;
  var context = useTooltipContext(PORTAL_NAME, __scopeTooltip);
  return /* @__PURE__ */jsx(PortalProvider, {
    scope: __scopeTooltip,
    forceMount: forceMount,
    children: /* @__PURE__ */jsx(Presence, {
      present: forceMount || context.open,
      children: /* @__PURE__ */jsx(PortalPrimitive, {
        asChild: true,
        container: container,
        children: children
      })
    })
  });
};
TooltipPortal.displayName = PORTAL_NAME;
var CONTENT_NAME = "TooltipContent";
var TooltipContent = React.forwardRef(function (props, forwardedRef) {
  var portalContext = usePortalContext(CONTENT_NAME, props.__scopeTooltip);
  var _props$forceMount = props.forceMount,
    forceMount = _props$forceMount === void 0 ? portalContext.forceMount : _props$forceMount,
    _props$side = props.side,
    side = _props$side === void 0 ? "top" : _props$side,
    contentProps = _objectWithoutProperties(props, _excluded2);
  var context = useTooltipContext(CONTENT_NAME, props.__scopeTooltip);
  return /* @__PURE__ */jsx(Presence, {
    present: forceMount || context.open,
    children: context.disableHoverableContent ? /* @__PURE__ */jsx(TooltipContentImpl, _objectSpread(_objectSpread({
      side: side
    }, contentProps), {}, {
      ref: forwardedRef
    })) : /* @__PURE__ */jsx(TooltipContentHoverable, _objectSpread(_objectSpread({
      side: side
    }, contentProps), {}, {
      ref: forwardedRef
    }))
  });
});
var TooltipContentHoverable = React.forwardRef(function (props, forwardedRef) {
  var context = useTooltipContext(CONTENT_NAME, props.__scopeTooltip);
  var providerContext = useTooltipProviderContext(CONTENT_NAME, props.__scopeTooltip);
  var ref = React.useRef(null);
  var composedRefs = useComposedRefs(forwardedRef, ref);
  var _React$useState5 = React.useState(null),
    _React$useState6 = _slicedToArray(_React$useState5, 2),
    pointerGraceArea = _React$useState6[0],
    setPointerGraceArea = _React$useState6[1];
  var trigger = context.trigger,
    onClose = context.onClose;
  var content = ref.current;
  var onPointerInTransitChange = providerContext.onPointerInTransitChange;
  var handleRemoveGraceArea = React.useCallback(function () {
    setPointerGraceArea(null);
    onPointerInTransitChange(false);
  }, [onPointerInTransitChange]);
  var handleCreateGraceArea = React.useCallback(function (event, hoverTarget) {
    var currentTarget = event.currentTarget;
    var exitPoint = {
      x: event.clientX,
      y: event.clientY
    };
    var exitSide = getExitSideFromRect(exitPoint, currentTarget.getBoundingClientRect());
    var paddedExitPoints = getPaddedExitPoints(exitPoint, exitSide);
    var hoverTargetPoints = getPointsFromRect(hoverTarget.getBoundingClientRect());
    var graceArea = getHull([].concat(_toConsumableArray(paddedExitPoints), _toConsumableArray(hoverTargetPoints)));
    setPointerGraceArea(graceArea);
    onPointerInTransitChange(true);
  }, [onPointerInTransitChange]);
  React.useEffect(function () {
    return function () {
      return handleRemoveGraceArea();
    };
  }, [handleRemoveGraceArea]);
  React.useEffect(function () {
    if (trigger && content) {
      var handleTriggerLeave = function handleTriggerLeave(event) {
        return handleCreateGraceArea(event, content);
      };
      var handleContentLeave = function handleContentLeave(event) {
        return handleCreateGraceArea(event, trigger);
      };
      trigger.addEventListener("pointerleave", handleTriggerLeave);
      content.addEventListener("pointerleave", handleContentLeave);
      return function () {
        trigger.removeEventListener("pointerleave", handleTriggerLeave);
        content.removeEventListener("pointerleave", handleContentLeave);
      };
    }
  }, [trigger, content, handleCreateGraceArea, handleRemoveGraceArea]);
  React.useEffect(function () {
    if (pointerGraceArea) {
      var handleTrackPointerGrace = function handleTrackPointerGrace(event) {
        var target = event.target;
        var pointerPosition = {
          x: event.clientX,
          y: event.clientY
        };
        var hasEnteredTarget = (trigger === null || trigger === void 0 ? void 0 : trigger.contains(target)) || (content === null || content === void 0 ? void 0 : content.contains(target));
        var isPointerOutsideGraceArea = !isPointInPolygon(pointerPosition, pointerGraceArea);
        if (hasEnteredTarget) {
          handleRemoveGraceArea();
        } else if (isPointerOutsideGraceArea) {
          handleRemoveGraceArea();
          onClose();
        }
      };
      document.addEventListener("pointermove", handleTrackPointerGrace);
      return function () {
        return document.removeEventListener("pointermove", handleTrackPointerGrace);
      };
    }
  }, [trigger, content, pointerGraceArea, onClose, handleRemoveGraceArea]);
  return /* @__PURE__ */jsx(TooltipContentImpl, _objectSpread(_objectSpread({}, props), {}, {
    ref: composedRefs
  }));
});
var _createTooltipContext7 = createTooltipContext(TOOLTIP_NAME, {
    isInside: false
  }),
  _createTooltipContext8 = _slicedToArray(_createTooltipContext7, 2),
  VisuallyHiddenContentContextProvider = _createTooltipContext8[0],
  useVisuallyHiddenContentContext = _createTooltipContext8[1];
var TooltipContentImpl = React.forwardRef(function (props, forwardedRef) {
  var __scopeTooltip = props.__scopeTooltip,
    children = props.children,
    ariaLabel = props["aria-label"],
    onEscapeKeyDown = props.onEscapeKeyDown,
    onPointerDownOutside = props.onPointerDownOutside,
    contentProps = _objectWithoutProperties(props, _excluded3);
  var context = useTooltipContext(CONTENT_NAME, __scopeTooltip);
  var popperScope = usePopperScope(__scopeTooltip);
  var onClose = context.onClose;
  React.useEffect(function () {
    document.addEventListener(TOOLTIP_OPEN, onClose);
    return function () {
      return document.removeEventListener(TOOLTIP_OPEN, onClose);
    };
  }, [onClose]);
  React.useEffect(function () {
    if (context.trigger) {
      var handleScroll = function handleScroll(event) {
        var target = event.target;
        if (target !== null && target !== void 0 && target.contains(context.trigger)) onClose();
      };
      window.addEventListener("scroll", handleScroll, {
        capture: true
      });
      return function () {
        return window.removeEventListener("scroll", handleScroll, {
          capture: true
        });
      };
    }
  }, [context.trigger, onClose]);
  return /* @__PURE__ */jsx(DismissableLayer, {
    asChild: true,
    disableOutsidePointerEvents: false,
    onEscapeKeyDown: onEscapeKeyDown,
    onPointerDownOutside: onPointerDownOutside,
    onFocusOutside: function onFocusOutside(event) {
      return event.preventDefault();
    },
    onDismiss: onClose,
    children: /* @__PURE__ */jsxs(PopperPrimitive.Content, _objectSpread(_objectSpread(_objectSpread({
      "data-state": context.stateAttribute
    }, popperScope), contentProps), {}, {
      ref: forwardedRef,
      style: _objectSpread(_objectSpread({}, contentProps.style), {
        "--radix-tooltip-content-transform-origin": "var(--radix-popper-transform-origin)",
        "--radix-tooltip-content-available-width": "var(--radix-popper-available-width)",
        "--radix-tooltip-content-available-height": "var(--radix-popper-available-height)",
        "--radix-tooltip-trigger-width": "var(--radix-popper-anchor-width)",
        "--radix-tooltip-trigger-height": "var(--radix-popper-anchor-height)"
      }),
      children: [/* @__PURE__ */jsx(Slottable, {
        children: children
      }), /* @__PURE__ */jsx(VisuallyHiddenContentContextProvider, {
        scope: __scopeTooltip,
        isInside: true,
        children: /* @__PURE__ */jsx(VisuallyHiddenPrimitive.Root, {
          id: context.contentId,
          role: "tooltip",
          children: ariaLabel || children
        })
      })]
    }))
  });
});
TooltipContent.displayName = CONTENT_NAME;
var ARROW_NAME = "TooltipArrow";
var TooltipArrow = React.forwardRef(function (props, forwardedRef) {
  var __scopeTooltip = props.__scopeTooltip,
    arrowProps = _objectWithoutProperties(props, _excluded4);
  var popperScope = usePopperScope(__scopeTooltip);
  var visuallyHiddenContentContext = useVisuallyHiddenContentContext(ARROW_NAME, __scopeTooltip);
  return visuallyHiddenContentContext.isInside ? null : /* @__PURE__ */jsx(PopperPrimitive.Arrow, _objectSpread(_objectSpread(_objectSpread({}, popperScope), arrowProps), {}, {
    ref: forwardedRef
  }));
});
TooltipArrow.displayName = ARROW_NAME;
function getExitSideFromRect(point, rect) {
  var top = Math.abs(rect.top - point.y);
  var bottom = Math.abs(rect.bottom - point.y);
  var right = Math.abs(rect.right - point.x);
  var left = Math.abs(rect.left - point.x);
  switch (Math.min(top, bottom, right, left)) {
    case left:
      return "left";
    case right:
      return "right";
    case top:
      return "top";
    case bottom:
      return "bottom";
    default:
      throw new Error("unreachable");
  }
}
function getPaddedExitPoints(exitPoint, exitSide) {
  var padding = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 5;
  var paddedExitPoints = [];
  switch (exitSide) {
    case "top":
      paddedExitPoints.push({
        x: exitPoint.x - padding,
        y: exitPoint.y + padding
      }, {
        x: exitPoint.x + padding,
        y: exitPoint.y + padding
      });
      break;
    case "bottom":
      paddedExitPoints.push({
        x: exitPoint.x - padding,
        y: exitPoint.y - padding
      }, {
        x: exitPoint.x + padding,
        y: exitPoint.y - padding
      });
      break;
    case "left":
      paddedExitPoints.push({
        x: exitPoint.x + padding,
        y: exitPoint.y - padding
      }, {
        x: exitPoint.x + padding,
        y: exitPoint.y + padding
      });
      break;
    case "right":
      paddedExitPoints.push({
        x: exitPoint.x - padding,
        y: exitPoint.y - padding
      }, {
        x: exitPoint.x - padding,
        y: exitPoint.y + padding
      });
      break;
  }
  return paddedExitPoints;
}
function getPointsFromRect(rect) {
  var top = rect.top,
    right = rect.right,
    bottom = rect.bottom,
    left = rect.left;
  return [{
    x: left,
    y: top
  }, {
    x: right,
    y: top
  }, {
    x: right,
    y: bottom
  }, {
    x: left,
    y: bottom
  }];
}
function isPointInPolygon(point, polygon) {
  var x = point.x,
    y = point.y;
  var inside = false;
  for (var i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    var xi = polygon[i].x;
    var yi = polygon[i].y;
    var xj = polygon[j].x;
    var yj = polygon[j].y;
    var intersect = yi > y !== yj > y && x < (xj - xi) * (y - yi) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}
function getHull(points) {
  var newPoints = points.slice();
  newPoints.sort(function (a, b) {
    if (a.x < b.x) return -1;else if (a.x > b.x) return 1;else if (a.y < b.y) return -1;else if (a.y > b.y) return 1;else return 0;
  });
  return getHullPresorted(newPoints);
}
function getHullPresorted(points) {
  if (points.length <= 1) return points.slice();
  var upperHull = [];
  for (var i = 0; i < points.length; i++) {
    var p = points[i];
    while (upperHull.length >= 2) {
      var q = upperHull[upperHull.length - 1];
      var r = upperHull[upperHull.length - 2];
      if ((q.x - r.x) * (p.y - r.y) >= (q.y - r.y) * (p.x - r.x)) upperHull.pop();else break;
    }
    upperHull.push(p);
  }
  upperHull.pop();
  var lowerHull = [];
  for (var _i = points.length - 1; _i >= 0; _i--) {
    var _p = points[_i];
    while (lowerHull.length >= 2) {
      var _q = lowerHull[lowerHull.length - 1];
      var _r = lowerHull[lowerHull.length - 2];
      if ((_q.x - _r.x) * (_p.y - _r.y) >= (_q.y - _r.y) * (_p.x - _r.x)) lowerHull.pop();else break;
    }
    lowerHull.push(_p);
  }
  lowerHull.pop();
  if (upperHull.length === 1 && lowerHull.length === 1 && upperHull[0].x === lowerHull[0].x && upperHull[0].y === lowerHull[0].y) {
    return upperHull;
  } else {
    return upperHull.concat(lowerHull);
  }
}
var Provider = TooltipProvider;
var Root3 = Tooltip;
var Trigger = TooltipTrigger;
var Portal = TooltipPortal;
var Content2 = TooltipContent;
var Arrow2 = TooltipArrow;
export { Arrow2 as Arrow, Content2 as Content, Portal, Provider, Root3 as Root, Tooltip, TooltipArrow, TooltipContent, TooltipPortal, TooltipProvider, TooltipTrigger, Trigger, createTooltipScope };